<script setup lang="ts">
// Pure SVG rendering of the detector plate: field frames, chamber dots,
// crosshair, corner labels and the zoom-to-selected-chamber transform. All data
// comes in via props; hover/selection are shared with the list via v-models.
import { computed, ref } from 'vue';
import type { ChamberDot, FieldFrame } from './useDetectorLayout';
import type { DeactivatedChamber } from './types';

type DeactivatedChamberModalGraphProps = {
  size: number;
  pad: number;
  dots: ChamberDot[];
  frames: FieldFrame[];
  deactivatedChambers: DeactivatedChamber[];
  showFrameLabels?: boolean;
  // fly-out choreography phases, driven by useZoomFromIcon in the parent
  zoomReady?: boolean;
  closing?: boolean;
};
const props = withDefaults(defineProps<DeactivatedChamberModalGraphProps>(), {
  showFrameLabels: false,
  zoomReady: false,
  closing: false,
});

const hoveredId = defineModel<number | undefined>('hoveredId');
const selectedChamberId = defineModel<number | undefined>('selectedChamberId');

const emit = defineEmits<{ (event: 'animationEnd'): void }>();

// exposed so the parent can measure the svg for the fly-out animation
const detectorSvg = ref<SVGSVGElement>();
defineExpose({ svgEl: detectorSvg });

const deactivatedById = computed(
  () => new Map(props.deactivatedChambers.map((chamber) => [chamber.id, chamber])),
);

// Deactivated chambers joined with their dot positions (skips ids the layout lacks)
const deactivatedDots = computed(() => {
  const dotById = new Map(props.dots.map((dot) => [dot.id, dot]));
  return props.deactivatedChambers.flatMap((chamber) => {
    const dot = dotById.get(chamber.id);
    return dot ? [{ ...dot, group: chamber.group }] : [];
  });
});

const hoveredDot = computed(() => props.dots.find((dot) => dot.id === hoveredId.value));

// The crosshair follows the hover and sticks to the selected chamber otherwise;
// it carries the chamber's group so the lines take on the same color
const crosshairDot = computed(() => {
  const dot = hoveredDot.value ?? props.dots.find((d) => d.id === selectedChamberId.value);
  if (!dot) return undefined;
  return { ...dot, group: deactivatedById.value.get(dot.id)?.group };
});

// Selection & zoom: a selected chamber zooms the graphic centered on it
// (clamped to the plate edges); clicking outside deselects and zooms out.
const ZOOM = 1.5;

const zoomTransform = computed(() => {
  const dot = props.dots.find((d) => d.id === selectedChamberId.value);
  if (!dot) return 'translate(0px, 0px) scale(1)';
  const clamp = (value: number) => Math.min(0, Math.max(-(ZOOM - 1) * props.size, value));
  const tx = clamp(props.size / 2 - ZOOM * dot.x);
  const ty = clamp(props.size / 2 - ZOOM * dot.y);
  return `translate(${tx}px, ${ty}px) scale(${ZOOM})`;
});

// Label badge flush in each frame's bottom-right corner: right/bottom edges lie
// on the frame lines, only the top-left corner is rounded (rect can't do
// per-corner radii, hence a path).
const FRAME_LABEL_FONT = 6; // keep in sync with .frame-label font-size
const labeledFrames = computed(() =>
  props.frames.map((frame) => {
    const corner = props.size / 2 + frame.half;
    // rough text width (SVG can't auto-size a shape to text)
    const w = frame.label.length * FRAME_LABEL_FONT * 0.55;
    const h = 9;
    const r = 2;
    return {
      ...frame,
      // text sits inside the badge (text-anchor: end)
      labelX: corner - 2,
      labelY: corner - 2.5,
      labelBgPath:
        `M ${corner - w + r} ${corner - h} H ${corner} V ${corner} H ${corner - w} ` +
        `V ${corner - h + r} A ${r} ${r} 0 0 1 ${corner - w + r} ${corner - h} Z`,
    };
  }),
);
</script>

<template>
  <svg
    ref="detectorSvg"
    class="detector"
    :class="{
      zoomed: !!selectedChamberId,
      'zoom-ready': zoomReady,
      'zoom-closing': closing,
    }"
    :viewBox="`0 0 ${size} ${size}`"
    role="img"
    :aria-label="$t('DetectorChamberMap')"
    @click="selectedChamberId = undefined"
    @animationend.self="emit('animationEnd')"
  >
    <g class="zoom-layer" :style="{ transform: zoomTransform }">
      <rect
        v-for="frame in labeledFrames"
        :key="frame.half"
        :x="frame.x"
        :y="frame.x"
        :width="frame.size"
        :height="frame.size"
        class="field-frame"
      />
      <template v-if="crosshairDot">
        <!-- crosshair stops a hair short of the plate edge -->
        <line
          :x1="pad - 4"
          :y1="crosshairDot.y"
          :x2="size - pad + 4"
          :y2="crosshairDot.y"
          class="crosshair"
          :class="crosshairDot.group === 'DC' ? 'group-dc' : 'group-uc'"
        />
        <line
          :x1="crosshairDot.x"
          :y1="pad - 4"
          :x2="crosshairDot.x"
          :y2="size - pad + 4"
          class="crosshair"
          :class="crosshairDot.group === 'DC' ? 'group-dc' : 'group-uc'"
        />
      </template>

      <circle
        v-for="dot in dots.filter((d) => !deactivatedById.has(d.id))"
        :key="dot.id"
        :cx="dot.x"
        :cy="dot.y"
        r="0.55"
        class="chamber"
      />
      <circle
        v-for="dot in deactivatedDots"
        :key="dot.id"
        :cx="dot.x"
        :cy="dot.y"
        r="1.5"
        class="chamber deactivated"
        :class="[
          dot.group === 'DC' ? 'group-dc' : 'group-uc',
          {
            hovered: hoveredId === dot.id,
            selected: selectedChamberId === dot.id,
          },
        ]"
        @mouseenter="hoveredId = dot.id"
        @mouseleave="hoveredId = undefined"
        @click.stop="selectedChamberId = dot.id"
      />

      <!-- drawn last: SVG paints in document order, so the label badges sit
           on top of the dot grid and crosshair -->
      <template v-if="showFrameLabels">
        <path
          v-for="frame in labeledFrames"
          :key="`label-bg-${frame.half}`"
          class="frame-label-bg"
          :d="frame.labelBgPath"
        />
        <text
          v-for="frame in labeledFrames"
          :key="`label-${frame.half}`"
          class="frame-label"
          :x="frame.labelX"
          :y="frame.labelY"
        >
          {{ frame.label }}
        </text>
      </template>
    </g>
  </svg>
</template>

<style scoped lang="scss">
// DC (PTW) = orange, UC (user) = blue; triplet so solid fills and translucent
// glows share one theme color
.group-dc {
  --group-rgb: var(--v-theme-warning);
}
.group-uc {
  --group-rgb: var(--v-theme-info);
}

@keyframes chamber-pulse {
  0%,
  100% {
    filter: drop-shadow(0 0 0.5px rgba(var(--group-rgb), 0.4));
  }
  50% {
    filter: drop-shadow(0 0 4px rgba(var(--group-rgb), 0.9));
  }
}

// translate moves the (scaled) detector center onto the button, so the graphic
// visibly departs from / returns to the button itself; the --grow-* vars are
// set by the parent card from useZoomFromIcon's measurements
@keyframes zoom-to-icon {
  from {
    transform: translate(0, 0) scale(1);
    border-radius: 16px;
  }
  to {
    transform: translate(var(--grow-tx, 0px), var(--grow-ty, 0px)) scale(var(--grow-scale, 0.04));
    border-radius: 24px;
  }
}

@keyframes zoom-from-icon {
  from {
    transform: translate(var(--grow-tx, 0px), var(--grow-ty, 0px)) scale(var(--grow-scale, 0.04));
    border-radius: 24px;
    box-shadow: 0 24px 64px rgba(0, 0, 0, 0.35);
  }
  to {
    transform: translate(0, 0) scale(1);
    border-radius: 16px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  }
}

.detector {
  flex: 0 0 auto;
  height: min(calc(100dvh - 48px), calc(100vw - 480px));
  aspect-ratio: 1;
  width: auto;
  max-width: none;
  border-radius: 16px;
  // primary-lighten-7 does not exist in the theme; surface (#FAFAFA) is the
  // lightest defined tone and keeps the plate a touch brighter than the card
  background: rgb(var(--v-theme-surface));
  box-shadow: inset 0 0 24px rgba(0, 0, 0, 0.06);
  // invisible until the zoom origin is measured, then grow out of the icon
  opacity: 0;

  &.zoom-ready {
    opacity: 1;
    // slow start so the tiny graphic visibly sits on the button first,
    // then sweeps out and settles gently into place
    animation: zoom-from-icon 0.6s cubic-bezier(0.55, 0.06, 0.25, 1);
  }

  &.zoom-closing {
    opacity: 1;
    animation: zoom-to-icon 0.3s cubic-bezier(0.55, 0.06, 0.25, 1) forwards;
  }

  &.zoomed {
    cursor: zoom-out;
  }

  @media (max-width: 1024px) {
    width: 100%;
    height: auto;
    margin-top: 48px;
  }
}

.zoom-layer {
  transform-origin: 0 0;
  transition: transform 1.1s cubic-bezier(0.16, 1, 0.3, 1);
}

.field-frame {
  fill: none;
  // one step lighter than the chamber dots so the line work stays distinct
  stroke: rgb(var(--v-theme-primary-lighten-3));
  stroke-width: 0.5;
}

.frame-label {
  // font-size must stay in sync with FRAME_LABEL_FONT (badge width estimate)
  fill: rgb(var(--v-theme-primary-lighten-2));
  font-size: 6px;
  text-anchor: end;
  pointer-events: none;
}

.frame-label-bg {
  fill: #fff;
  // same grey as the field-frame boxes it labels
  stroke: rgb(var(--v-theme-primary-lighten-3));
  stroke-width: 0.5;
  // badges sit above the chamber dots; don't steal their hover/click
  pointer-events: none;
}

.crosshair {
  // takes the group color (orange DC / blue UC) via the group-dc/group-uc class
  stroke: rgb(var(--group-rgb, var(--v-theme-error)));
  stroke-width: 0.9;
  stroke-dasharray: 2.5 2;
  opacity: 0.85;
}

.chamber {
  fill: rgb(var(--v-theme-primary-lighten-2));
  pointer-events: none;

  &.deactivated {
    pointer-events: auto;
    transition:
      fill 0.2s ease,
      r 0.2s ease,
      stroke-width 0.2s ease;
    fill: rgb(var(--group-rgb));
    animation: chamber-pulse 1.6s ease-in-out infinite;
    cursor: pointer;
  }

  // highlighted (hovered or selected): enlarged dot with a semi-transparent
  // halo in the group color; the halo is a wide stroke around the circle
  &.deactivated.hovered,
  &.deactivated.selected {
    animation-play-state: paused;
    r: 2.5;
    stroke: rgba(var(--group-rgb), 0.3);
    stroke-width: 3;
  }
}
</style>
